import { UiNode } from '@ory/client';
import { Divider, Loader, Paper, PasswordInput, Stack, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { Button } from 'src/components';
import { AuthFeedback } from 'src/auth/components/AuthFeedback';
import {
  PasswordRequirements,
  passwordSchema,
  allPasswordRequirementsMet,
  PasswordFormValues,
} from 'src/components/convenience/auth/PasswordRequirements';
import { OidcSection } from 'src/components/convenience/auth/OidcSection';
import { PublicTenantConfig } from 'src/auth/hooks/usePublicTenantConfig';

interface ChangePasswordFormProps {
  error: string | null;
  isSubmitting: boolean;
  onSubmit: (password: string) => void;
  oidcNodes: UiNode[];
  formAction: string;
  csrfToken: string;
  tenantConfig: PublicTenantConfig | undefined;
  isLoadingTenantConfig?: boolean;
}

export const ChangePasswordForm = ({
  error,
  isSubmitting,
  onSubmit,
  oidcNodes,
  formAction,
  csrfToken,
  tenantConfig,
  isLoadingTenantConfig,
}: ChangePasswordFormProps) => {
  const form = useForm<PasswordFormValues>({
    mode: 'controlled',
    initialValues: {
      password: '',
    },
    validate: zodResolver(passwordSchema),
    validateInputOnChange: true,
  });

  const password = form.values.password;
  const requirementsMet = allPasswordRequirementsMet(password);

  const handleSubmit = (values: PasswordFormValues) => {
    onSubmit(values.password);
  };

  if (isLoadingTenantConfig) {
    return (
      <Paper bg="gray.0" radius="xl" py={48} px={48} w={450}>
        <Stack gap="md" align="center">
          <Loader />
        </Stack>
      </Paper>
    );
  }

  const isSsoOnly = tenantConfig?.authMethod === 'sso_only';

  return (
    <Paper bg="gray.0" radius="xl" py={48} px={48} w={450}>
      <Stack gap="lg">
        <Text fz="xl" fw={600} ta="center">
          Manage Credentials
        </Text>

        {!isSsoOnly && (
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <Text size="sm" fw={500} ta="center">
                Change your password
              </Text>

              <PasswordInput
                label="New password"
                placeholder="Enter new password"
                withAsterisk
                autoFocus
                key={form.key('password')}
                {...form.getInputProps('password')}
                error={false}
              />

              <PasswordRequirements password={password} />

              <AuthFeedback error={error} />

              <Button
                type="submit"
                fullWidth
                loading={isSubmitting}
                disabled={!requirementsMet || isSubmitting}
              >
                Update password
              </Button>
            </Stack>
          </form>
        )}

        {!isSsoOnly && <Divider />}

        <Stack gap="sm">
          <Text size="sm" fw={500} ta="center">
            Manage your accounts
          </Text>

          <OidcSection nodes={oidcNodes} formAction={formAction} csrfToken={csrfToken} />
        </Stack>
      </Stack>
    </Paper>
  );
};
